// ─── Eligibility Screening Logic ───
// 7-step eligibility gate that runs before financial optimization

/**
 * Check employee eligibility according to plan rules
 * @param {Object} employee - Employee data from CSV
 * @param {Object} employerPlanRules - Employer's plan eligibility rules
 * @param {Object} optimal - Optimal plan result from financial optimization (null if not yet calculated)
 * @returns {Object} - { eligible, status, reason, eligibleDate, isHCE, isKeyEmployee }
 */
export function checkEligibility(employee, employerPlanRules, optimal = null) {
  const today = new Date();
  
  // Step 1: Worker Type Check
  if (employee.workerType === '1099') {
    return {
      eligible: false,
      status: '1099',
      reason: '1099 contractors cannot participate in Section 125 plans',
      eligibleDate: null,
      isHCE: false,
      isKeyEmployee: false,
    };
  }
  
  // Step 2: S-Corp Ownership Check (>2% shareholders excluded)
  if (employee.ownership > 2) {
    return {
      eligible: false,
      status: 'owner',
      reason: 'S-corp shareholders owning >2% are excluded from Section 125',
      eligibleDate: null,
      isHCE: false,
      isKeyEmployee: true, // >2% owners are always key employees
    };
  }
  
  // Step 3: Employment Class Check
  // Note: We don't have seasonal/temp flag in CSV yet, so skip this check for now
  // Will add when we implement seasonal employee tracking
  
  // Union check
  if (employee.isUnion && !employerPlanRules.eligible_classes.union_cba) {
    return {
      eligible: false,
      status: 'union',
      reason: 'Union employees excluded per plan document',
      eligibleDate: null,
      isHCE: false,
      isKeyEmployee: false,
    };
  }
  
  // Step 4: Hours Check
  if (employee.weeklyHours < employerPlanRules.minimum_weekly_hours) {
    return {
      eligible: false,
      status: 'hours',
      reason: `Below minimum hours requirement (${employee.weeklyHours}/wk, need ${employerPlanRules.minimum_weekly_hours}/wk)`,
      eligibleDate: null,
      isHCE: false,
      isKeyEmployee: false,
    };
  }
  
  // Step 5: Waiting Period Check
  if (employee.hireDate && employerPlanRules.waiting_period_days > 0) {
    const daysSinceHire = Math.floor((today - employee.hireDate) / (1000 * 60 * 60 * 24));
    
    if (daysSinceHire < employerPlanRules.waiting_period_days) {
      const eligibleDate = new Date(employee.hireDate);
      eligibleDate.setDate(eligibleDate.getDate() + employerPlanRules.waiting_period_days);
      
      return {
        eligible: false,
        status: 'waiting',
        reason: `In waiting period`,
        eligibleDate: eligibleDate,
        isHCE: false,
        isKeyEmployee: false,
      };
    }
  }
  
  // Step 6: Financial Viability Check (requires optimal plan result)
  if (optimal && !optimal.isViable) {
    return {
      eligible: false,
      status: 'salary',
      reason: `Salary too low for net-positive benefit`,
      breakEvenSalary: optimal.breakEvenSalary || null,
      eligibleDate: null,
      isHCE: checkIsHCE(employee, employerPlanRules),
      isKeyEmployee: checkIsKeyEmployee(employee, employerPlanRules),
    };
  }
  
  // Step 7: HCE Classification (flag, not exclusion)
  const isHCE = checkIsHCE(employee, employerPlanRules);
  const isKeyEmployee = checkIsKeyEmployee(employee, employerPlanRules);
  
  // Employee is eligible!
  return {
    eligible: true,
    status: 'eligible',
    reason: null,
    eligibleDate: null,
    isHCE: isHCE,
    isKeyEmployee: isKeyEmployee,
  };
}

/**
 * Determine if employee is a Highly Compensated Employee (HCE)
 */
function checkIsHCE(employee, employerPlanRules) {
  // Check override first
  if (employee.hceOverride !== null) {
    return employee.hceOverride;
  }
  
  // Otherwise calculate from salary threshold
  return employee.salary >= employerPlanRules.hce_salary_threshold;
}

/**
 * Determine if employee is a Key Employee
 * Per IRS: Officer with compensation >$XXX, >5% owner, or >1% owner with comp >$150k
 */
function checkIsKeyEmployee(employee, employerPlanRules) {
  // >5% owner
  if (employee.ownership > employerPlanRules.key_employee_ownership_threshold) {
    return true;
  }
  
  // >1% owner with high compensation
  if (employee.ownership > 1 && employee.salary > 150000) {
    return true;
  }
  
  // Note: We don't have "officer" flag in CSV, so we can't check that condition
  // Will add when needed
  
  return false;
}

/**
 * Get display-friendly status label
 */
export function getEligibilityStatusLabel(eligibility, optimal = null) {
  if (eligibility.eligible) {
    return {
      label: `✓ Eligible${optimal ? ` — ${optimal.label}` : ''}`,
      color: 'green',
      icon: 'checkmark-circle',
    };
  }
  
  switch (eligibility.status) {
    case 'waiting':
      const dateStr = eligibility.eligibleDate ? eligibility.eligibleDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '';
      return {
        label: `⏳ Eligible ${dateStr}`,
        color: 'orange',
        icon: 'time',
      };
    case '1099':
      return {
        label: '✗ 1099 — Not eligible',
        color: 'red',
        icon: 'close-circle',
      };
    case 'hours':
      return {
        label: `✗ ${Math.round(eligibility.reason.match(/(\d+)\/wk/)?.[1] || 0)} hrs — Need ${eligibility.reason.match(/need (\d+)/)?.[1] || 0}`,
        color: 'red',
        icon: 'close-circle',
      };
    case 'owner':
      return {
        label: '✗ >2% shareholder',
        color: 'red',
        icon: 'close-circle',
      };
    case 'union':
      return {
        label: '✗ Union excluded',
        color: 'red',
        icon: 'close-circle',
      };
    case 'salary':
      return {
        label: eligibility.breakEvenSalary ? `✗ Need $${Math.round(eligibility.breakEvenSalary).toLocaleString()}` : '✗ Salary too low',
        color: 'red',
        icon: 'close-circle',
      };
    default:
      return {
        label: '✗ Not eligible',
        color: 'red',
        icon: 'close-circle',
      };
  }
}

/**
 * Calculate eligibility statistics for a workforce
 */
export function calculateEligibilityStats(employees) {
  const stats = {
    total: employees.length,
    eligible: 0,
    waiting: 0,
    ineligible_1099: 0,
    ineligible_hours: 0,
    ineligible_owner: 0,
    ineligible_union: 0,
    ineligible_salary: 0,
    hce_count: 0,
    key_employee_count: 0,
  };
  
  employees.forEach(emp => {
    if (emp.eligibility) {
      if (emp.eligibility.eligible) {
        stats.eligible++;
      } else {
        switch (emp.eligibility.status) {
          case 'waiting':
            stats.waiting++;
            break;
          case '1099':
            stats.ineligible_1099++;
            break;
          case 'hours':
            stats.ineligible_hours++;
            break;
          case 'owner':
            stats.ineligible_owner++;
            break;
          case 'union':
            stats.ineligible_union++;
            break;
          case 'salary':
            stats.ineligible_salary++;
            break;
        }
      }
      
      if (emp.eligibility.isHCE) {
        stats.hce_count++;
      }
      if (emp.eligibility.isKeyEmployee) {
        stats.key_employee_count++;
      }
    }
  });
  
  stats.ineligible = stats.total - stats.eligible - stats.waiting;
  stats.participation_rate = stats.total > 0 ? (stats.eligible / stats.total) * 100 : 0;
  
  return stats;
}

/**
 * Check if CSV has eligibility columns
 */
export function hasEligibilityColumns(employees) {
  if (!employees || employees.length === 0) return { hasAny: false, missing: [] };
  
  const sample = employees[0];
  const missing = [];
  
  // Check for key eligibility fields
  if (!sample.hireDate) missing.push('Hire Date');
  if (sample.weeklyHours === 40 && sample.hireDate === null) missing.push('Weekly Hours');
  if (sample.workerType === 'W-2' && sample.hireDate === null) missing.push('Worker Type');
  
  return {
    hasAny: missing.length < 3, // If at least one field is present
    missing: missing,
  };
}
